// /brokers/lifecycle — broker approvals and lifecycle (SM-716, SM-621
// phase 08a).
//
// The nodes that need a lifecycle decision, one queue per own status:
// drafts awaiting approval (the default), paused nodes to resume or wind
// down, closing nodes to close once their obligations clear, and the
// active ones — where pause and the emergency disable live. Every verb
// is a confirm dialog with a mandatory reason and answers with
// broker-service's audit reference; a closure refusal lists each
// blocking obligation with its currency, amount and what clears it.
//
// Reads need BROKER_VIEW; each verb its own code (approve BROKER_APPROVE;
// pause, resume and emergency disable BROKER_PAUSE; begin-close and
// close BROKER_CLOSE). broker-service re-checks every one.
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { BrokerLifecycleControls } from "@/components/brokers/BrokerLifecycleControls";
import { brokerName, brokerStatusSummary } from "@/components/brokers/governance";
import { msToIso } from "@/components/brokers/receivables";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Pagination } from "@/components/ui/Pagination";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchBrokers } from "@/lib/broker-queries";
import { fmtDate } from "@/lib/format";
import { Perm } from "@/lib/perms";

const PAGE_SIZE = 25;

type Queue = "DRAFT" | "PAUSED" | "CLOSING" | "ACTIVE";

const QUEUES: Queue[] = ["DRAFT", "PAUSED", "CLOSING", "ACTIVE"];

interface Search {
  queue?: Queue;
  page?: number;
}

export const Route = createFileRoute("/_app/brokers/lifecycle")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    queue: QUEUES.find((q) => q === s.queue),
    page: Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
  }),
  component: BrokerLifecyclePage,
});

const th = "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

const QUEUE_COPY: Record<Queue, { label: string; empty: string }> = {
  DRAFT: { label: "Awaiting approval", empty: "No broker is waiting for approval." },
  PAUSED: { label: "Paused", empty: "No broker is paused." },
  CLOSING: { label: "Closing", empty: "No broker is closing." },
  ACTIVE: { label: "Active", empty: "No broker is active." },
};

function BrokerLifecyclePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canView = perms.has(Perm.BrokerView);
  const queue: Queue = search.queue ?? "DRAFT";
  const page = search.page ?? 0;
  const filter = { status: queue, limit: PAGE_SIZE, offset: page * PAGE_SIZE };

  const q = useQuery({
    queryKey: brokerKeys.list(filter),
    queryFn: () => fetchBrokers(filter),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
    enabled: canView,
  });
  const rows = q.data?.items ?? [];
  const mayAct = perms.hasAny(Perm.BrokerApprove, Perm.BrokerPause, Perm.BrokerClose);

  if (perms.loaded && !canView) {
    return <EmptyState title="No access" description="You need the BROKER_VIEW permission to see broker lifecycles." />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Approvals and lifecycle</h1>
        <p className="text-sm text-muted">
          Approve new brokers, pause or resume live ones, and wind down the ones that are closing. Emergency disable is
          a pause recorded as an emergency, and nothing else: cancels, withdrawals, settlement and payouts carry on.
        </p>
      </div>

      <SegmentedControl<Queue>
        label="Queue"
        value={queue}
        onChange={(v) => void navigate({ search: { queue: v === "DRAFT" ? undefined : v } })}
        options={QUEUES.map((k) => ({ value: k, label: QUEUE_COPY[k].label }))}
      />

      {perms.loaded && !mayAct ? (
        <p className="text-2xs text-muted">
          You can review these brokers, but running a lifecycle action needs BROKER_APPROVE, BROKER_PAUSE or
          BROKER_CLOSE.
        </p>
      ) : null}

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>{QUEUE_COPY[queue].label}</CardTitle>
          <span className="text-2xs text-muted">{q.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          <p className="border-b border-border px-5 py-2 text-2xs text-muted">{brokerStatusSummary(queue)}</p>
          {q.isLoading || !perms.loaded ? (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : null}
          {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
          {q.data && rows.length === 0 ? (
            <p className="px-5 py-16 text-center text-sm text-muted">{QUEUE_COPY[queue].empty}</p>
          ) : null}
          {rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Broker</th>
                    <th className={th}>Place in the tree</th>
                    <th className={th}>In this status since</th>
                    <th className={th}>Reason</th>
                    <th className={`${th} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.broker_id} className="border-b border-border/60 align-top last:border-0">
                      <td className={`${td} px-5`}>
                        <Link to="/brokers/$id" params={{ id: b.broker_id }} className="font-medium text-t-1 hover:underline">
                          {brokerName(b)}
                        </Link>
                        <div className="font-mono text-2xs text-muted">{b.broker_id}</div>
                      </td>
                      <td className={td}>
                        {b.parent_broker_id ? (
                          <div className="space-y-0.5 text-2xs text-muted">
                            <div>Depth {b.broker_depth}</div>
                            <Link to="/brokers/$id" params={{ id: b.parent_broker_id }} className="font-mono hover:underline">
                              {b.parent_broker_id}
                            </Link>
                          </div>
                        ) : (
                          <Badge tone="accent">Root</Badge>
                        )}
                      </td>
                      <td className={`${td} text-2xs text-muted`}>{fmtDate(msToIso(b.status_changed_at_unix_ms))}</td>
                      <td className={`${td} text-2xs`}>{b.status_reason || "—"}</td>
                      <td className={`${td} text-right`}>
                        <div className="flex justify-end">
                          <BrokerLifecycleControls broker={b} size="sm" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {q.data && q.data.page.total > 0 ? (
            <Pagination
              page={q.data.page}
              currentPage={page}
              onPageChange={(p) => void navigate({ search: (s) => ({ ...s, page: p > 0 ? p : undefined }) })}
              itemLabel="entries"
            />
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
